import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild, Output, EventEmitter, OnDestroy, input, inject } from '@angular/core';
import { CodemirrorComponent } from '../../modules/codemirror/codemirror.component';
// ─────────────────────────────────────────────────────────────────────────────
// Toolbars
import { MARKDOWN_ACTIONS } from './toolbars/markdown.toolbar';
import { JAVASCRIPT_ACTIONS } from './toolbars/javascript.toolbar';
import { PYTHON_ACTIONS } from './toolbars/python.toolbar';
import { DEBUG_ACTIONS } from './toolbars/debug.toolbar';
import { EDITOR_ACTIONS } from './toolbars/editor.toolbar';
// ─────────────────────────────────────────────────────────────────────────────
// Formatter
import { js_beautify } from 'js-beautify';
// ─────────────────────────────────────────────────────────────────────────────
// CM6 commands
import { undo, redo } from '@codemirror/commands';
import { openSearchPanel } from '@codemirror/search';
import { EditorView } from '@codemirror/view';
// ─────────────────────────────────────────────────────────────────────────────
import { PythonService } from '../../services/python/python.service';
import { ToastrService } from 'ngx-toastr';
import { AutoUnsubscribe } from '../../decorators/auto-unsubscribe.decorator';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';

const MODE_MARKDOWN = 'markdown';
const MODE_JAVASCRIPT = 'javascript';
const MODE_JSON = 'json';
const MODE_PYTHON = 'python';
const MODE_RHAI = 'rhai';
const MODE_DEBUG = 'debug';

export type CMActionEvent = {
  action: string;
  codeMirror: any;
};

@AutoUnsubscribe()
@Component({
  standalone: true,
  selector: 'app-code-editor',
  templateUrl: './code-editor.component.html',
  styleUrls: ['./code-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    // Code mirror
    CodemirrorComponent,
    // Forms support
    ReactiveFormsModule,
    FormsModule,
    // Material
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    // Layout
    FlexLayoutModule,
  ],
})
export class CodeEditorComponent implements OnInit, OnChanges, OnDestroy {
  private readonly pythonService = inject(PythonService);
  private readonly toastrService = inject(ToastrService);
  private readonly cdr = inject(ChangeDetectorRef);

  showToolbar: boolean = false;
  toolbarContent: any[] = [];

  showSubToolbar: boolean = false;
  subToolbarContent: any[] = [];

  // Linting is wired in the CodeMirror wrapper via a lint compartment
  // (ESLint for JavaScript, pyflakes for Python). See CodeLintService. (EHQ-108)
  options: any = {
    lineNumbers: true,
    lineWrapping: true,
    foldGutter: true,
    styleActiveLine: { nonEmpty: true },
    matchBrackets: true,
    autoCloseBrackets: true,
    theme: 'eclipse', // Default to light theme
  };

  @Input() mode!: string;
  @Input() content!: string;
  minimumHeight = input(300);
  maximumHeight = input(0);

  private _readOnly = false;
  @Input() get readOnly(): boolean {
    return this._readOnly;
  }
  set readOnly(value: boolean) {
    this._readOnly = value;
    if (this.options) {
      this.options.readOnly = value;
      // Same-reference mutation, so the wrapper's [options] setter cannot fire.
      // Push it explicitly. (EHQ-171)
      this.codeMirror?.applyOptions(this.options);
    }
  }

  @Output() updateContent: EventEmitter<any> = new EventEmitter();
  @Output() startEdit: EventEmitter<any> = new EventEmitter();
  @Output() codeEditorLoaded: EventEmitter<any> = new EventEmitter();
  @Output() codeEditorAction: EventEmitter<CMActionEvent> = new EventEmitter();
  @ViewChild(CodemirrorComponent, { static: false }) codeMirror!: CodemirrorComponent;

  /** Get the CM6 EditorView from the wrapper component */
  private get view(): EditorView | undefined {
    return this.codeMirror?.editorView;
  }

  ngOnInit() {
    // Register SVG icons early in constructor to ensure they're available when template renders
    this.setToolbarVisibility();
    this.getDefaultOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['content'] && changes['content'].currentValue !== undefined) {
      const next = changes['content'].currentValue ?? '';
      if (this.content !== next) {
        this.content = next;
        this.cdr.markForCheck();
      }
    }
    // A dynamic [mode] binding (the step-form dialog swaps it when the user
    // changes the step type) has to be pushed into the options object and the
    // toolbars rebuilt, then handed to the wrapper so it reconfigures the
    // language/tabSize/lint compartments. Without this the editor keeps the
    // mode it was created with, so e.g. a step switched from JavaScript to
    // Python stays JS-highlighted and keeps ESLint as its lint source, which
    // reports bogus fatal parse errors on valid Python.
    //
    // applyOptions() must be called explicitly: options is mutated in place, so
    // the reference never changes and the wrapper's [options] setter cannot
    // fire. This used to ride on the wrapper's ngDoCheck differ, which Angular
    // 22 stopped invoking on later parent updates.
    // (EHQ-149, follow-up to the mode-keyed linting added in EHQ-108;
    // re-fixed for Angular 22 in EHQ-171)
    if (changes['mode'] && !changes['mode'].firstChange) {
      this.options.mode = this.mode;
      this.setToolbarVisibility();
      this.codeMirror?.applyOptions(this.options);
      this.cdr.markForCheck();
    }
  }

  // eslint-disable-next-line
  ngOnDestroy(): void {
    // This method is intentionally empty
  }

  getDefaultOptions() {
    this.options.mode = this.mode;
  }

  handleChange(data: string) {
    this.updateContent.emit(data);
  }

  touchEditor() {
    this.startEdit.emit();
  }

  // ─────────────────────────────────────────────────────────────────────
  // Markdown toolbar

  private registerIcons(_actions: any[]): void {
    // Icons are now registered globally via IconRegistryService in app.config.ts
    // This method is kept for backward compatibility but does nothing
    // The icons are already available from the global registry
  }

  // Called on init and on every subsequent mode change, so it has to clear the
  // previous mode's toolbars rather than only switch things on — otherwise a
  // step retyped from Markdown to Python keeps the Markdown toolbar, and one
  // retyped from JavaScript keeps js-beautify instead of py-beautify. (EHQ-149)
  private setToolbarVisibility() {
    this.showToolbar = false;
    this.toolbarContent = [];
    this.showSubToolbar = false;
    this.subToolbarContent = [];

    switch (this.mode) {
      case MODE_MARKDOWN: {
        this.showToolbar = true;
        this.toolbarContent = MARKDOWN_ACTIONS;
        this.showSubToolbar = true;
        this.subToolbarContent = EDITOR_ACTIONS;
        break;
      }
      // JSON shares the JavaScript toolbar: js_beautify pretty-prints JSON, and
      // search/undo/redo are language-agnostic. Keeping these together means the
      // json mode added for EHQ-108 only drops linting, not the toolbar.
      case MODE_JAVASCRIPT:
      case MODE_JSON: {
        this.registerIcons(JAVASCRIPT_ACTIONS);
        this.showSubToolbar = true;
        this.subToolbarContent = [...JAVASCRIPT_ACTIONS, ...EDITOR_ACTIONS];
        break;
      }
      case MODE_PYTHON: {
        this.registerIcons(PYTHON_ACTIONS);
        this.showSubToolbar = true;
        this.subToolbarContent = [...PYTHON_ACTIONS, ...EDITOR_ACTIONS];
        break;
      }
      // Rhai has no formatter, so only the language-agnostic search/undo/redo.
      case MODE_RHAI: {
        this.showSubToolbar = true;
        this.subToolbarContent = EDITOR_ACTIONS;
        break;
      }
      case MODE_DEBUG: {
        this.showSubToolbar = true;
        this.subToolbarContent = DEBUG_ACTIONS;
        break;
      }
      default:
        break;
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // CM6 helper: get selected text or fallback
  private getSelection(fallback: string = ''): string {
    const v = this.view;
    if (!v) return fallback;
    const { from, to } = v.state.selection.main;
    if (from === to) return fallback;
    return v.state.sliceDoc(from, to);
  }

  // CM6 helper: replace selection
  private replaceSelection(text: string): void {
    const v = this.view;
    if (!v) return;
    v.dispatch(v.state.replaceSelection(text));
    v.focus();
  }

  public processToolbarCommand(name: string) {
    const commandMap: Record<string, () => void> = {
      'bold': () => this.handleBoldCommand(),
      'italic': () => this.handleItalicCommand(),
      'underline': () => this.handleUnderlineCommand(),
      'heading': () => this.handleHeadingCommand(),
      'reference': () => this.handleReferenceCommand(),
      'strikethrough': () => this.handleStrikethroughCommand(),
      'link': () => this.handleLinkCommand(),
      'image': () => this.handleImageCommand(),
      'ul': () => this.handleUlCommand(),
      'ol': () => this.handleOlCommand(),
      'code': () => this.handleCodeCommand(),
      'clear-code': () => this.handleClearCodeCommand(),
      'js-beautify': () => this.handleJsBeautifyCommand(),
      'py-beautify': () => this.handlePyBeautifyCommand(),
      'redo': () => this.handleRedoCommand(),
      'undo': () => this.handleUndoCommand(),
      'search': () => this.handleSearchCommand(),
    };

    const handler = commandMap[name];
    if (handler) {
      handler();
    } else {
      this.codeEditorAction.emit({ action: name, codeMirror: this.view });
    }
  }

  private handleBoldCommand(): void {
    const selectedText = this.getSelection('Bold Text');
    this.replaceSelection(`**${selectedText}**`);
  }

  private handleItalicCommand(): void {
    const selectedText = this.getSelection('Italic Text');
    this.replaceSelection(`*${selectedText}*`);
  }

  private handleUnderlineCommand(): void {
    const selectedText = this.getSelection('Underline Text');
    this.replaceSelection(`<ins>${selectedText}</ins>`);
  }

  private handleHeadingCommand(): void {
    const selectedText = this.getSelection('Heading');
    this.replaceSelection(`# ${selectedText}`);
  }

  private handleReferenceCommand(): void {
    const selectedText = this.getSelection('Reference');
    this.replaceSelection(`> ${selectedText}`);
  }

  private handleStrikethroughCommand(): void {
    const selectedText = this.getSelection('Strikethrough Text');
    this.replaceSelection(`~~${selectedText}~~`);
  }

  private handleLinkCommand(): void {
    this.replaceSelection(`[](http://)`);
  }

  private handleImageCommand(): void {
    this.replaceSelection(`![](http://)`);
  }

  private handleUlCommand(): void {
    const selectedText = this.getSelection('');
    this.replaceSelection(`- ${selectedText}`);
  }

  private handleOlCommand(): void {
    const selectedText = this.getSelection('');
    this.replaceSelection(`1. ${selectedText}`);
  }

  private handleCodeCommand(): void {
    const selectedText = this.getSelection('Source Code');
    this.replaceSelection(`\`\`\`language\r\n${selectedText}\r\n\`\`\``);
  }

  private handleClearCodeCommand(): void {
    this.content = '';
  }

  private handleJsBeautifyCommand(): void {
    this.content = js_beautify(this.content);
    this.cdr.markForCheck();
  }

  private handlePyBeautifyCommand(): void {
    this.pythonService
      .formatCode(this.content)
      .then((code: string) => {
        this.content = code;
        this.cdr.markForCheck();
        this.toastrService.success(
          'Python code formatted successfully.',
          'Formatting Complete'
        );

        this.updateContent.emit(this.content);
      })
      .catch((error: any) => {
        // Provide more specific error messages
        if (error.message && error.message.includes('Syntax Error:')) {
          this.toastrService.warning(
            `Python syntax error: ${error.message}. Please fix the syntax before formatting.`,
            'Formatting Failed'
          );
        } else {
          this.toastrService.warning(
            'Python code could not be autoformatted. The original code will be preserved.',
            'Formatting Failed'
          );
        }
      });
  }

  private handleRedoCommand(): void {
    const v = this.view;
    if (v) {
      redo(v);
      v.focus();
    }
  }

  private handleUndoCommand(): void {
    const v = this.view;
    if (v) {
      undo(v);
      v.focus();
    }
  }

  private handleSearchCommand(): void {
    const v = this.view;
    if (v) {
      openSearchPanel(v);
    }
  }

  public codeMirrorLoaded(editor: any) {
    this.codeEditorLoaded.emit(editor);
  }

  /**
   * Select [from, to], focus the editor and scroll the location into view.
   * The editor grows to fit its content (no internal scroll), so the native
   * scrollIntoView on the line's DOM node is what actually scrolls the
   * surrounding container into position. (SCRUM-708)
   */
  public revealRange(from: number, to: number): void {
    const v = this.view;
    if (!v) {
      return;
    }
    const max = v.state.doc.length;
    const anchor = Math.max(0, Math.min(from, max));
    const head = Math.max(anchor, Math.min(to, max));
    v.dispatch({ selection: { anchor, head }, scrollIntoView: true });
    v.focus();
    // Defer so CM has rendered around the new selection before we measure it.
    requestAnimationFrame(() => {
      const node = v.domAtPos(anchor).node;
      const el =
        node.nodeType === Node.TEXT_NODE
          ? (node.parentElement as HTMLElement | null)
          : (node as HTMLElement);
      el?.scrollIntoView({ block: 'center' });
    });
  }
}
